import { useState } from 'react';
import { InviteMemberForm, UseInviteMemberReturnType } from '../subscription.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  BillingUser,
  PaymentGatewayEnum,
  PaymentIntentPurposeEnum,
  PlanSubscription,
  RoleEnum,
  useCheckMemberEmailExistLazyQuery,
  useEstimateMemberAdditionMutation,
  useListSubscriptionQuery,
  useSubscribeWorkspacePlanAndInviteUserMutation,
} from '@/types/generated/graphql';
import { useModal } from '@shared/hooks/use-modal';
import { useCreatePaymentIntent } from '@modules/settings/wallet/hooks/use-create-payment-intent';
import { notification } from 'antd';
import { parseChargebeeErrorMessage } from '@shared/utils/parseChargebeeErrorMessage';
import { inviteMemberSchema } from '../schema/subscription.schema';
import { cacheOnly } from '@shared/constants';
import { usePaymentMethodsList } from './use-payment-methods-list';
import { incrementTotalMemberCountInCache } from '@modules/settings/members/member-handlers';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';

export const useInviteMember = (): UseInviteMemberReturnType => {
  const { t } = useTranslation();
  const [errorAlert, setErrorAlert] = useState('');
  const [payloads, setPayloads] = useState<BillingUser | null>(null);
  const [estimateAmount, setEstimateAmount] = useState(0);

  const inviteModal = useModal();
  const paymentModal = useModal();
  const downgradeModal = useModal();
  const subsCancelledModal = useModal();
  const paymentMethodsList = usePaymentMethodsList();

  const listSubscriptionData = useListSubscriptionQuery(cacheOnly);
  const { createPaymentIntent } = useCreatePaymentIntent();

  const form = useForm<InviteMemberForm>({
    resolver: zodResolver(inviteMemberSchema(t)),
  });

  const [estimateMemberAddition, estimateState] = useEstimateMemberAdditionMutation({
    onError: (error) => {
      notification.error({
        message: error?.message,
      });
    },
  });

  const [inviteMember] = useSubscribeWorkspacePlanAndInviteUserMutation({
    onCompleted: async (data) => {
      notification.success({
        message: capitalize(t('invitation_sent_successfully')),
        description: data.subscribeWorkspacePlanAndInviteUser?.message,
      });
      paymentModal.handleClose();
      inviteModal.handleClose();
      form.reset();
      incrementTotalMemberCountInCache();
    },
    refetchQueries: ['ListSubscription'],
    update(cache) {
      cache.modify({
        fields: {
          invoicesV2: () => {},
        },
      });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'workspaceMembersV2' });
      cache.gc();
    },
    onError: (error) => {
      notification.error({ message: error?.message });
      paymentModal.handleClose();
    },
  });

  async function handleCreatePaymentIntent() {
    const isPayPal = paymentMethodsList?.paymentMethod?.paypal?.email !== null;
    if (isPayPal) {
      return {
        amount: estimateAmount,
        gateway_account_id: paymentMethodsList?.paymentMethod?.gatewayAccountId,
      };
    }

    const intentResponse = await createPaymentIntent({
      paymentIntentPurpose: PaymentIntentPurposeEnum?.add_workspace_member,
      paymentGateway: PaymentGatewayEnum?.stripe,
      addWorkspaceMember: {
        estimatedAmount: estimateAmount,
        referenceId: paymentMethodsList?.paymentMethod?.referenceId || '',
      },
      gatewayAccountId: paymentMethodsList?.paymentMethod?.gatewayAccountId,
    });

    if (intentResponse?.errors && intentResponse.errors.length > 0) {
      throw new Error(intentResponse?.data?.createPaymentIntent?.message ?? capitalize(t('error')));
    }

    const intent = intentResponse?.data?.createPaymentIntent?.data;

    const cbInstance = window?.Chargebee?.getInstance?.();
    //@ts-expect-error Chargebee types not available
    const threeDSHandler = await cbInstance.load3DSHandler();
    threeDSHandler.setPaymentIntent(intent);
    return await threeDSHandler.handleCardPayment();
  }

  const subscriptions = listSubscriptionData?.data?.listSubscription?.edges;
  const scheduledSubscription = useScheduledSubscription(subscriptions);

  const handlePaymentProcess = async () => {
    paymentMethodsList?.setProcessing(true);
    setErrorAlert('');

    try {
      const isPayPal = paymentMethodsList?.paymentMethod?.paypal?.email !== null;
      const intentToCharge = await handleCreatePaymentIntent();

      if (intentToCharge?.active_payment_attempt?.error_text) {
        const { message } = parseChargebeeErrorMessage(intentToCharge.active_payment_attempt.error_text);
        setErrorAlert(message);
        return;
      }

      if (!payloads) return;

      const extraInputs = isPayPal
        ? { referenceId: paymentMethodsList?.paymentMethod?.referenceId }
        : { gatewayToken: intentToCharge?.active_payment_attempt?.id_at_gateway };

      const paymentResult = await inviteMember({
        variables: {
          input: {
            gatewayAccountId: isPayPal
              ? paymentMethodsList?.paymentMethod?.gatewayAccountId
              : intentToCharge?.gateway_account_id,
            gatewayToken: intentToCharge?.active_payment_attempt?.id_at_gateway,
            user: payloads,
            paymentGateway: isPayPal ? PaymentGatewayEnum.paypal : PaymentGatewayEnum.stripe,
            ...extraInputs,
          },
        },
      });

      paymentMethodsList?.setProcessing(false);

      return paymentResult;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('an_unexpected_error_occurred');
      setErrorAlert(errorMessage);
    } finally {
      paymentMethodsList?.setProcessing(false);
    }
  };

  const checkScheduledDowngrade = () => {
    if (scheduledSubscription?.isCancelled) {
      subsCancelledModal.handleOpen();
    } else if (scheduledSubscription?.hasScheduledChanges) {
      downgradeModal.handleOpen();
    } else {
      handlePaymentProcess();
    }
  };

  const [checkEmailExist] = useCheckMemberEmailExistLazyQuery({
    fetchPolicy: 'network-only',
  });

  const onSubmit = async (data: InviteMemberForm) => {
    const payloads = {
      name: data?.name,
      email: data?.email,
      role: data?.role as RoleEnum,
    };

    try {
      const emailExistResult = await checkEmailExist({ variables: { email: data?.email.toLowerCase() } });

      if (emailExistResult?.data?.checkMemberEmailExist?.data?.exists) {
        return notification.warning({ message: t('you_cannot_reinvite_existing_member') });
      }

      const estimateResult = await estimateMemberAddition();
      const estimateAmount = estimateResult?.data?.estimateMemberAddition?.data?.estimatedAmount;
      if (!estimateAmount) return;

      setEstimateAmount(estimateAmount);
      setPayloads(payloads);
      paymentModal.handleOpen();
    } catch (err) {
      console.error('There was a error sending invitation', err);
    }
  };

  return {
    form,
    inviteModal,
    paymentModal,
    downgradeModal,
    subsCancelledModal,
    onSubmit,
    estimateState,
    estimateAmount,
    payloads,
    paymentMethodsList,
    errorAlert,
    checkScheduledDowngrade,
    scheduledSubscription,
    handlePaymentProcess,
  };
};

function useScheduledSubscription(subscriptions?: (PlanSubscription | null)[] | null) {
  const workspaceSubscription = subscriptions?.find((data) => data?.itemProductFamily === 'calilio-workspace');
  const isCancelled = workspaceSubscription?.status === 'non_renewing';
  const { hasScheduledChanges = false } = workspaceSubscription || {};

  return {
    isCancelled,
    hasScheduledChanges,
  };
}

export type UseScheduledSubscriptionReturn = ReturnType<typeof useScheduledSubscription>;
